
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth";
import ProfileInfoForm from "@/components/profile/ProfileInfoForm";
import ProfileLocationForm from "@/components/profile/ProfileLocationForm";
import ProfilePrivacyInfo from "@/components/profile/ProfilePrivacyInfo";
import { Loader2 } from "lucide-react";

const Profile = () => {
  const { profile, isLoading, refreshProfile } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<string>("personal");
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refresh profile data when component mounts
  useEffect(() => {
    const loadProfile = async () => {
      setIsRefreshing(true);
      try {
        await refreshProfile();
      } catch (error) {
        console.error("Error refreshing profile:", error);
        toast({
          title: "Error loading profile",
          description: "There was a problem loading your profile information",
          variant: "destructive"
        });
      } finally {
        setIsRefreshing(false);
      }
    };
    
    loadProfile();
  }, [refreshProfile, toast]);

  if (isLoading || isRefreshing) {
    return (
      <div className="container flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container py-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Profile Settings</h1>
        
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
            <CardDescription>
              Manage your personal information and preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="personal">Personal Info</TabsTrigger>
                <TabsTrigger value="location">Location Settings</TabsTrigger>
              </TabsList>
              
              <TabsContent value="personal" className="space-y-6">
                <ProfileInfoForm profile={profile} onUpdate={refreshProfile} />
                <ProfilePrivacyInfo />
              </TabsContent>
              
              <TabsContent value="location" className="space-y-6">
                <ProfileLocationForm profile={profile} onUpdate={refreshProfile} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
